if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      inventoryRequestPending = false;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        if (this.id.startsWith('MainProduct-')) {
          this.inventoryPageShowHandler = (event) => {
            if (event.persisted) this.revalidateCurrentSoldOut();
          };
          window.addEventListener('pageshow', this.inventoryPageShowHandler);
          queueMicrotask(() => this.revalidateCurrentSoldOut());
        }

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.abortController?.abort();
        window.removeEventListener('pageshow', this.inventoryPageShowHandler);
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          inventoryOptions: !shouldSwapProduct && this.id.startsWith('MainProduct-') ? selectedOptionValues : null,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode)
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode, updateUrl = true) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          if (updateUrl) this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant
              }
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      inventoryOptionValues(picker = this.variantSelectors) {
        return Array.from(picker?.querySelectorAll('select option:checked, fieldset input:checked') || []).map(
          (input) => input.dataset.optionValueId
        );
      }

      // Only complete native HTML for this product and exact selection can replace availability.
      inventoryVariant(html, optionValues, variantId) {
        const product = html.getElementById(this.id);
        if (!product || product.dataset.productId !== this.productId || product.dataset.section !== this.sectionId) return null;
        if (!this.dataset.inventoryCountry || product.dataset.inventoryCountry !== this.dataset.inventoryCountry ||
            this.dataset.inventoryCountry !== window.Shopify?.country) return null;
        const picker = product.querySelector('variant-selects');
        if (!picker || html.querySelector('variant-selects') !== picker) return null;
        if (JSON.stringify(this.inventoryOptionValues(picker)) !== JSON.stringify(optionValues)) return null;
        let variant;
        try {
          variant = this.getSelectedVariant(product);
        } catch (_) {
          return null;
        }
        if (!variant || typeof variant.available !== 'boolean' || (variantId && variant.id !== variantId)) return null;
        const selectedValues = Array.from(picker.querySelectorAll('select option:checked, fieldset input:checked')).map(
          (input) => input.value
        );
        if (JSON.stringify(variant.options) !== JSON.stringify(selectedValues)) return null;
        const button = product.querySelector(`[id="ProductSubmitButton-${this.sectionId}"]`);
        if (!button || button.hasAttribute('disabled') === variant.available) return null;
        for (const id of ['price', 'Inventory', 'Badges']) {
          if (this.querySelector(`[id="${id}-${this.sectionId}"]`) && !product.querySelector(`[id="${id}-${this.sectionId}"]`)) return null;
        }
        return variant;
      }

      inventoryRequestIsCurrent(controller) {
        return this.isConnected && this.abortController === controller && !controller.signal.aborted;
      }

      async revalidateInventory(requestUrl, optionValues, variantId, controller) {
        const url = new URL(requestUrl, location.origin);
        const productUrl = new URL(this.dataset.url, location.origin);
        if (url.origin !== location.origin || url.pathname !== productUrl.pathname) return null;
        const pageUrl = location.href;
        const country = window.Shopify?.country;
        url.searchParams.set('inventory_check', `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        const retryController = new AbortController();
        const abortRetry = () => retryController.abort();
        controller.signal.addEventListener('abort', abortRetry, { once: true });
        const timeout = setTimeout(abortRetry, 8000);
        try {
          if (!this.inventoryRequestIsCurrent(controller)) return null;
          const response = await fetch(url.href, {
            cache: 'no-store',
            credentials: 'same-origin',
            signal: retryController.signal
          });
          if (response.status !== 200 || !response.headers.get('content-type')?.includes('text/html')) return null;
          const finalUrl = new URL(response.url);
          if (finalUrl.origin !== url.origin || finalUrl.pathname !== url.pathname) return null;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!this.inventoryRequestIsCurrent(controller) || retryController.signal.aborted || location.href !== pageUrl ||
              window.Shopify?.country !== country) return null;
          if (JSON.stringify(this.inventoryOptionValues()) !== JSON.stringify(optionValues)) return null;
          return this.inventoryVariant(html, optionValues, variantId) ? html : null;
        } catch (_) {
          // Retain the original native sold-out state if fresh evidence is unavailable.
          return null;
        } finally {
          clearTimeout(timeout);
          controller.signal.removeEventListener('abort', abortRetry);
        }
      }

      async revalidateCurrentSoldOut() {
        if (!this.isConnected || this.inventoryRequestPending || !this.id.startsWith('MainProduct-')) return;
        const optionValues = this.inventoryOptionValues();
        if (!optionValues.length || optionValues.some((value) => !value)) return;
        const variant = this.inventoryVariant(document, optionValues);
        if (variant?.available !== false) return;
        this.abortController?.abort();
        const controller = (this.abortController = new AbortController());
        this.inventoryRequestPending = true;
        try {
          const html = await this.revalidateInventory(
            this.buildRequestUrlWithParams(this.dataset.url, optionValues), optionValues, variant.id, controller
          );
          if (html && this.inventoryRequestIsCurrent(controller)) {
            this.handleUpdateProductInfo(this.dataset.url, this.dataset.viewMode || 'main-product', false)(html);
          }
        } finally {
          if (this.abortController === controller) this.inventoryRequestPending = false;
        }
      }

      async renderProductInfo({ requestUrl, targetId, callback, inventoryOptions = null }) {
        this.abortController?.abort();
        const controller = (this.abortController = new AbortController());
        this.inventoryRequestPending = true;
        try {
          const response = await fetch(requestUrl, { signal: controller.signal });
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!this.inventoryRequestIsCurrent(controller)) return;
          this.pendingRequestUrl = null;
          const variant = response.status === 200 && inventoryOptions?.length
            ? this.inventoryVariant(html, inventoryOptions)
            : null;
          callback(html);
          document.querySelector(`#${targetId}`)?.focus();
          if (variant?.available === false) {
            const refreshed = await this.revalidateInventory(requestUrl, inventoryOptions, variant.id, controller);
            if (refreshed && this.inventoryRequestIsCurrent(controller)) callback(refreshed);
          }
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        } finally {
          if (this.abortController === controller) this.inventoryRequestPending = false;
        }
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;

        const parsedUrl = new URL(url, window.location.origin);

        if (variantId) {
          parsedUrl.searchParams.set('variant', variantId);
        } else {
          parsedUrl.searchParams.delete('variant');
        }

        window.history.replaceState({}, '', parsedUrl);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId
          }
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML
          }
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
